import React, { useMemo } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { IgButton } from "@intelligems/ig-theme";
import {
  getDeprecatedRemainingTrialDays,
  getRemainingTrialDays,
} from "@intelligems/ig-utils";
import { useGetOrganizationSummary } from "@src/api/organization-summaries/get-organization-summary";
import { useGetOrg } from "@src/api/organizations/get-organization";

import { SETTINGS_ROUTES } from "../../../routes/routes";
import { dismissAlert } from "../../../store/slices/appSettings";
import { IgInfo } from "../../Utils/IgInfo";

export const FreeTrialStatusAlert = () => {
  const org = useGetOrg();
  const orgSummary = useGetOrganizationSummary();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { mode, remainingTrialDays } = useMemo(() => {
    let mode: string | null = null;
    let remainingTrialDays: number = 0;

    if (!orgSummary.isSuccess) {
      return { mode, remainingTrialDays };
    }

    const deprecatedRemainingTrialDays = getDeprecatedRemainingTrialDays(
      org.data.org.freeTrialExpiresAtTs,
      org.data.org.createdAtTs
    );

    if (
      orgSummary.data.org.stripeSubscriptionSummaries.some(
        (ss) => ss.status === "trialing"
      )
    ) {
      mode = "trialing";
      remainingTrialDays = getRemainingTrialDays(orgSummary.data.org);
    } else if (
      orgSummary.data.org.organizationSubscriptions.some(
        (sub) => sub.status === "active"
      )
    ) {
      if (deprecatedRemainingTrialDays > 0) {
        mode = "trialing";
        remainingTrialDays = deprecatedRemainingTrialDays;
      } else {
        mode = "active";
      }
    } else if (orgSummary.data.org.stripeSubscriptionSummaries.length === 0) {
      mode = "noSubscription";
      remainingTrialDays = getRemainingTrialDays(orgSummary.data.org);
    }

    return { mode, remainingTrialDays };
  }, [orgSummary.data, orgSummary.isSuccess]);
  if (!orgSummary.isSuccess) {
    return null;
  }

  if (
    (mode === "trialing" || mode === "noSubscription") &&
    orgSummary.data.org.organizationSubscriptions.length > 0 &&
    orgSummary.data.org.stripeSubscriptionSummaries.length > 0
  ) {
    return (
      <IgInfo
        dismissId={"free-trial-pending"}
        title={
          mode === "noSubscription"
            ? "Activate your plan"
            : "Free Trial Activated"
        }
        message={
          remainingTrialDays > 0
            ? `You have ${remainingTrialDays} ${
                remainingTrialDays !== 1 ? "days" : "day"
              } left in your free trial.`
            : `Your free trial has expired`
        }
        severity={remainingTrialDays > 0 ? "info" : "warning"}
        buttons={
          mode === "noSubscription"
            ? [
                <IgButton
                  key={`button-1`}
                  variant={"contained"}
                  onClick={() => {
                    void navigate(SETTINGS_ROUTES.Settings + "#manage-plans");
                    dispatch(
                      dismissAlert({
                        orgId: orgSummary.data.org.id,
                        alertId: "free-trial-pending",
                      })
                    );
                  }}
                >
                  Activate
                </IgButton>,
              ]
            : []
        }
      />
    );
  }

  return null;
};
